import { useChartTheme } from '../config/chartTheme';
import { ColorModeToggle } from './ColorModeToggle';
import { BrandLogo } from './BrandLogo';
import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  Avatar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  PostAdd as PostAddIcon,
  History as HistoryIcon,
  Lightbulb as IdeaIcon,
  Link as LinkIcon,
  Logout as LogoutIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  FactCheck as ActivityIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  HelpOutline as HelpIcon,
  AdminPanelSettings as AdminIcon,
  Campaign as MarketingIcon,
  VideoLibrary as RecordingsIcon,
  MenuBook as ResourcesIcon,
  InsertChartOutlined as StatsIcon,
  Tune as TuneIcon,
  NewReleases as ChangelogIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { ConnectionAttentionBanner } from './ConnectionAttentionBanner';
import { SiteFooter } from './SiteFooter';
import { ScrollToTop } from './ScrollToTop';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

// Which sidebar groups the user left open. Cosmetic, so a read failure is ignored.
const GROUPS_KEY = 'shoutvector.openNavGroups';

const drawerWidth = 240;
const miniDrawerWidth = 65;

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Every chart in the app takes its fonts, gridlines and tooltip from the MUI theme
  useChartTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // One level of nesting only. A group has no path of its own — clicking it expands rather
  // than navigating, so there is never a landing page that exists just to hold links.
  type NavItem = {
    text: string;
    icon: React.ReactElement;
    path?: string;
    adminOnly?: boolean;
    /** Hidden from the LinkedIn reviewer, who has no use for it (and cannot create API keys). */
    hideForDemo?: boolean;
    children?: NavItem[];
  };

  const menuItems: NavItem[] = ([
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Create Post', icon: <PostAddIcon />, path: '/create' },
    // Directly below Create Post because that is how the app is actually used: you write a
    // post, then you look at what you have written. It sat under Analytics until 2026-09-26,
    // which filed it as something you go and analyse rather than part of composing.
    // Between Create Post and Post History: an idea is a post before it has a date, so it sits
    // between writing one and reviewing what went out.
    { text: 'Idea Staging', icon: <IdeaIcon />, path: '/ideas' },
    { text: 'Post History', icon: <HistoryIcon />, path: '/history' },
    // Scheduled Posts used to be its own page but was a stub that always said there was
    // nothing scheduled. The calendar shows the real queue, so it is the only entry now.
    { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
    // Flat rather than an Analytics group. Once Post History moved out, that group held Stats
    // and an admin-only Activity Log — so a non-admin got an expander concealing a single
    // item, which costs a click and saves no rows.
    { text: 'Stats', icon: <StatsIcon />, path: '/analytics' },
    { text: 'Activity Log', icon: <ActivityIcon />, path: '/activity', adminOnly: true },
    {
      text: 'Resources',
      icon: <ResourcesIcon />,
      children: [
        // The weekly rhythm is reference material — set up once and consulted, not worked in
        // daily like Create Post or the Calendar. Ian moved it here on 2026-09-26; it sat at
        // the top level above Calendar before.
        { text: 'Posting Schedule', icon: <ScheduleIcon />, path: '/schedule' },
        { text: 'User Guide', icon: <HelpIcon />, path: '/guide' },
        { text: 'API Docs', icon: <DescriptionIcon />, path: '/api-documentation', hideForDemo: true },
        { text: "What's Changed", icon: <ChangelogIcon />, path: '/changelog' },
      ],
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      // The group itself is not admin-only: Platform Connections is usable by any signed-in
      // user, and gating the parent would hide it from them entirely. Misc Config keeps its
      // own admin flag, so a non-admin sees a Settings group holding just Connections.
      children: [
        { text: 'Platform Connections', icon: <LinkIcon />, path: '/connections' },
        { text: 'Misc Config', icon: <TuneIcon />, path: '/settings', adminOnly: true },
      ],
    },
    {
      text: 'Admin',
      icon: <AdminIcon />,
      adminOnly: true,
      children: [
        { text: 'Users', icon: <PeopleIcon />, path: '/users', adminOnly: true },
        { text: 'Marketing Tools', icon: <MarketingIcon />, path: '/marketing-tools', adminOnly: true },
        { text: 'Recordings', icon: <RecordingsIcon />, path: '/recordings', adminOnly: true },
      ],
    },
  ] as NavItem[])
    .filter(item => !item.adminOnly || user?.role === 'admin')
    .map(item =>
      item.children
        ? {
            ...item,
            children: item.children.filter(c =>
              (!c.adminOnly || user?.role === 'admin') && !(c.hideForDemo && user?.role === 'demo')),
          }
        : item
    )
    // A group whose every child was filtered out would render as a dead expander
    .filter(item => !item.children || item.children.length > 0);

  // Groups stay collapsed by default — the point of grouping is a shorter sidebar, and
  // opening everything on load would make it longer than the flat list it replaced. What you
  // last opened is remembered, so the menu does not keep re-closing on you.
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    let remembered: string[] = [];
    try {
      const saved = localStorage.getItem(GROUPS_KEY);
      if (saved) remembered = JSON.parse(saved);
    } catch {
      // A corrupt or unavailable store is not worth failing the whole layout over
    }
    // The group holding the current page is always open, so a deep link never lands you on a
    // page whose section looks shut.
    const containingCurrent = menuItems
      .filter(i => i.children?.some(c => c.path === location.pathname))
      .map(i => i.text);
    return Array.from(new Set([...remembered, ...containingCurrent]));
  });

  const toggleGroup = (text: string) =>
    setOpenGroups(prev => {
      const next = prev.includes(text) ? prev.filter(t => t !== text) : [...prev, text];
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        // Persisting is a convenience; losing it must not break the click
      }
      return next;
    });

  const drawer = (isOpen: boolean) => (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: isOpen ? 'flex-start' : 'center', py: 1 }}>
          {isOpen ? (
            <BrandLogo height={40} />
          ) : (
            <img
              src="/shoutvector_logo_sym.png"
              alt="SV"
              style={{ height: '40px', width: 'auto' }}
            />
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          const go = (path: string) => {
            navigate(path);
            setMobileOpen(false);
          };

          if (!item.children) {
            return (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={isOpen ? '' : item.text} placement="right" arrow>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => go(item.path!)}
                    sx={{ justifyContent: isOpen ? 'initial' : 'center', px: isOpen ? 2 : 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 2 : 0, justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {isOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          }

          const expanded = openGroups.includes(item.text);
          const childActive = item.children.some(c => c.path === location.pathname);

          // Collapsed to the icon rail there is no room to nest, so the group's children are
          // shown flat and the group header is dropped. Its label survives in each tooltip.
          if (!isOpen) {
            return (
              <React.Fragment key={item.text}>
                {item.children.map(child => (
                  <ListItem key={child.text} disablePadding>
                    <Tooltip title={`${item.text} · ${child.text}`} placement="right" arrow>
                      <ListItemButton
                        selected={location.pathname === child.path}
                        onClick={() => go(child.path!)}
                        sx={{ justifyContent: 'center', px: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                          {child.icon}
                        </ListItemIcon>
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => toggleGroup(item.text)}
                  // Highlighted when collapsed over the active page, so the section still
                  // shows where you are once it is shut
                  selected={childActive && !expanded}
                  sx={{ px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>
              </ListItem>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {item.children.map(child => (
                    <ListItem key={child.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === child.path}
                        onClick={() => go(child.path!)}
                        sx={{ pl: 5, pr: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{ justifyContent: isOpen ? 'initial' : 'center', px: isOpen ? 2 : 1 }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 2 : 0, justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
            {isOpen && <ListItemText primary="Logout" />}
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${desktopOpen ? drawerWidth : miniDrawerWidth}px)` },
          ml: { sm: `${desktopOpen ? drawerWidth : miniDrawerWidth}px` },
          transition: 'width 0.3s, margin-left 0.3s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'block', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ShoutVector
          </Typography>
          {/* Sits beside the account button so the company you are acting as is always on
              screen — publishing to the wrong one is the mistake worth designing against. */}
          <ColorModeToggle />
          <WorkspaceSwitcher />
          <Button
            color="inherit"
            onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
            startIcon={
              <Avatar
                src={user?.avatarUrl || undefined}
                sx={{ width: 28, height: 28, fontSize: '0.85rem' }}
              >
                {(user?.name || '?').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
              </Avatar>
            }
            sx={{ textTransform: 'none' }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {user?.name}
            </Box>
          </Button>
          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={() => setAccountMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAccountMenuAnchor(null); navigate('/profile'); }}>
              <MenuItemIcon><AccountIcon fontSize="small" /></MenuItemIcon>
              Your account
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAccountMenuAnchor(null); handleLogout(); }}>
              <MenuItemIcon><LogoutIcon fontSize="small" /></MenuItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: desktopOpen ? drawerWidth : miniDrawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer(true)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : miniDrawerWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer(desktopOpen)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          width: { sm: `calc(100% - ${desktopOpen ? drawerWidth : miniDrawerWidth}px)` },
          // A flex item defaults to `min-width: auto`, which means "at least as wide as my
          // widest child" — so one wide table or code block stretched <main> past the
          // viewport and took the whole page sideways with it. Above sm the explicit width
          // above masked this; on a phone there is no width, so nothing stopped it.
          //
          // minWidth: 0 lets the item be narrower than its content, which is what makes the
          // overflowX containers inside it actually scroll instead of expanding their parent.
          minWidth: 0,
          // Belt and braces: even with the above, a single unbreakable token (an 80-character
          // URL in the API docs) can still push past. Clip at the shell rather than let the
          // body scroll.
          maxWidth: '100%',
          overflowX: 'hidden',
          transition: 'width 0.3s',
        }}
      >
        <Toolbar />
        {/* 24px each side costs 48px of a 390px phone. 16px there, unchanged from sm up. */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          {/* Demo accounts are handed to platform reviewers; make the limits obvious */}
          {user?.role === 'demo' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>LinkedIn Demo Account.</strong> You can compose and publish to LinkedIn.
              Platform connections are read-only, and user and API key management is
              unavailable on this account.
            </Alert>
          )}
          <ConnectionAttentionBanner />
          {children}
        </Box>
        <SiteFooter />
      </Box>

      {/* Outside <main> so the shell's overflowX: hidden cannot clip a fixed element, and
          mounted once here rather than per page. */}
      <ScrollToTop />
    </Box>
  );
};
